import * as React from "react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronDown, Loader } from "lucide-react";
import { GenerateReviewFormData } from "./GenerateReviewForm";
import { useDocument } from "@/hooks/useDocument";
import { useDefinitions } from "@/hooks/useDefinitions";
const TilesList = React.lazy(() => import("./TilesList"));
import { useGenerateReview } from "@/hooks/useGenerateReview";
import { GenerateReviewRequest } from "@/types/review.types";
import { useFuzzySearch } from "@/hooks/useFuzzySearch";

const GenerateReviewForm = React.lazy(() =>
  import("./GenerateReviewForm").then((module) => ({ default: module.GenerateReviewForm }))
);

interface ReviewProps {
  onReviewComplete?: () => void;
}

function formatTime(timeInSeconds: number): string {
  const minutes = Math.floor(timeInSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
  return `(${minutes}:${seconds})`;
}

function useElapsedTimer(isRunning: boolean): number {
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    if (isRunning) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning]);

  return elapsedTime;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
}

const ReviewPopoverContent: React.FC<{
  selectedOptions: Set<string>;
  onOptionChange: (keys: string[]) => void;
  onClose: () => void;
  onGenerate: (data: GenerateReviewFormData) => void;
  isGenerating: boolean;
}> = ({ selectedOptions, onOptionChange, onClose, onGenerate, isGenerating }) => (
  <React.Suspense
    fallback={
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader className="animate-spin" size={48} />
        <p>Loading...</p>
      </div>
    }
  >
    <GenerateReviewForm
      initialSelectedOptions={selectedOptions}
      onOptionSelected={onOptionChange}
      onCancel={onClose}
      onGenerate={onGenerate}
      isGenerating={isGenerating}
    />
  </React.Suspense>
);

function buildReviewRequests(
  data: GenerateReviewFormData,
  documentId: string,
  documentTitle: string
) {
  return data.selectedOptions.map((queryTerm) => {
    const controller = new AbortController();
    const reviewRequest: GenerateReviewRequest = {
      document_id: [documentId],
      document_filename: [documentTitle],
      client: data.representingInput?.trim() ?? "",
      consider: data.considerationInput?.trim() ?? "",
      current_document_id: documentId,
      query_term: queryTerm,
    };
    return { request: reviewRequest, controller };
  });
}

function executeReviewMutations(
  requestsWithControllers: { request: GenerateReviewRequest; controller: AbortController }[],
  generateReviewMutation: ReturnType<typeof useGenerateReview>,
  setIsGenerating: (v: boolean) => void,
  setAbortControllers: (controllers: AbortController[]) => void,
  buildIndex: () => void,
  onReviewComplete?: () => void
) {
  setAbortControllers(requestsWithControllers.map((item) => item.controller));
  const mutations = requestsWithControllers.map(({ request, controller }) =>
    generateReviewMutation.mutateAsync({ request, signal: controller.signal })
  );
  // Each rejection is caught to prevent one failed review type from cancelling others.
  // Individual errors are already toasted by useGenerateReview's onError handler.
  Promise.all(mutations.map((p) => p.catch((e) => e))).finally(() => {
    setIsGenerating(false);
    setAbortControllers([]);
    buildIndex();
    onReviewComplete?.();
  });
}

const ReviewOptionsPopover: React.FC<{
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  selectedOptions: Set<string>;
  onOptionChange: (keys: string[]) => void;
  onGenerate: (data: GenerateReviewFormData) => void;
  isGenerating: boolean;
}> = ({
  isPopoverOpen,
  setIsPopoverOpen,
  selectedOptions,
  onOptionChange,
  onGenerate,
  isGenerating,
}) => (
  <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
    <PopoverTrigger asChild>
      <div
        role="button"
        className="w-full flex font-medium items-center rounded-md justify-between px-4 py-2 text-sm h-10 border"
      >
        Select review options
        <ChevronDown className="ml-2 size-3.5" />
      </div>
    </PopoverTrigger>
    <PopoverContent
      forceMount
      align="center"
      side="bottom"
      collisionPadding={20}
      className="overflow-y-auto p-4 max-h-[calc(100vh-200px)]"
      style={{ width: "var(--radix-popover-trigger-width)" }}
    >
      <ReviewPopoverContent
        selectedOptions={selectedOptions}
        onOptionChange={onOptionChange}
        onClose={() => setIsPopoverOpen(false)}
        onGenerate={onGenerate}
        isGenerating={isGenerating}
      />
    </PopoverContent>
  </Popover>
);

const Review: React.FC<ReviewProps> = ({ onReviewComplete }) => {
  const [selectedOptions, setSelectedOptions] = React.useState<Set<string>>(new Set());
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [abortControllers, setAbortControllers] = React.useState<AbortController[]>([]);
  const elapsedTime = useElapsedTimer(isGenerating);

  const { data: document } = useDocument();
  useDefinitions();
  const generateReviewMutation = useGenerateReview();
  const { buildIndex } = useFuzzySearch();

  const debouncedSelectedOptions = useDebouncedValue(selectedOptions, 1500);

  const handleGenerateReview = (data: GenerateReviewFormData) => {
    if (document?._id == null || document?.title == null) return;
    setIsGenerating(true);
    const requests = buildReviewRequests(data, document._id, document.title);
    executeReviewMutations(
      requests,
      generateReviewMutation,
      setIsGenerating,
      setAbortControllers,
      buildIndex,
      onReviewComplete
    );
  };

  const handleCancelGenerating = () => {
    abortControllers.forEach((controller) => controller.abort());
    setAbortControllers([]);
    setIsGenerating(false);
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex justify-end gap-2">
        <ReviewOptionsPopover
          isPopoverOpen={isPopoverOpen}
          setIsPopoverOpen={setIsPopoverOpen}
          selectedOptions={selectedOptions}
          onOptionChange={(keys) => setSelectedOptions(new Set(keys))}
          onGenerate={handleGenerateReview}
          isGenerating={isGenerating}
        />
      </div>
      {isGenerating && (
        <Button variant="destructive" onClick={handleCancelGenerating} className="w-full">
          Cancel {formatTime(elapsedTime)}
        </Button>
      )}
      {document?._id != null && (
        <React.Suspense
          fallback={
            <div className="flex justify-center items-center h-[200px]">
              <Loader className="animate-spin" />
              <span className="ml-2">Loading...</span>
            </div>
          }
        >
          <TilesList
            selectedQueryTerms={Array.from(debouncedSelectedOptions)}
            documentId={document._id}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default Review;
